/**
 * [T-062] ร่างข้อความฝั่ง server — message_drafts (ตารางมีอยู่ตั้งแต่ migration 0000 · 04 §3 Draft)
 * PUT/GET/DELETE /api/v1/tickets/:id/draft + GET /api/v1/me/drafts (เมนู "ร่าง N")
 * self-contained (import แค่ zod + type จาก index) เหมือน submodule อื่น — index.ts ดึงผ่าน export บรรทัดเดียว
 */
import { z } from "zod";
import type { TicketSummary } from "./index.ts";

export const DraftKind = z.enum(["public", "internal"]);
export type DraftKind = z.infer<typeof DraftKind>;

/** [T-062 R4] เพดานขนาดร่าง — เกิน = 413 payload_too_large (ดูจุดเช็คใน routes ของ worker) */
export const MAX_DRAFT_BODY_BYTES = 256 * 1024;

/** PUT /api/v1/tickets/:id/draft — upsert (idempotent) คืน {updated_at} */
export const SaveDraft = z.object({
  kind: DraftKind,
  to: z.array(z.email()).max(50).optional(),
  cc: z.array(z.email()).max(48).optional(),
  bcc: z.array(z.email()).max(48).optional(),
  body_html: z.string(),
  attachments: z.array(z.uuid()).max(20).optional(),
  shared: z.boolean().optional(),
  /** [T-062 R3] CAS — ค่า updated_at ที่ client ถืออยู่ · server ใหม่กว่า = 409 พร้อมร่างปัจจุบัน · ไม่ส่ง = ทับได้ */
  if_unmodified_since: z.iso.datetime().optional(),
});
export type SaveDraft = z.infer<typeof SaveDraft>;

export const DraftQuery = z.object({ kind: DraftKind });
export type DraftQuery = z.infer<typeof DraftQuery>;

/** ร่างที่คืนออกไป — body_html sanitize ตอนอ่าน (เก็บของผู้ใช้ไว้ตามจริง — R4) */
export const DraftPayload = z.object({
  kind: DraftKind,
  to: z.array(z.string()),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
  body_html: z.string(),
  attachments: z.array(z.uuid()),
  shared: z.boolean(),
  updated_at: z.iso.datetime(),
});
export type DraftPayload = z.infer<typeof DraftPayload>;

/** GET /api/v1/tickets/:id/draft?kind= — ของฉัน + ของคนอื่นที่ shared=true (แยกกันชัดเจน) */
export const DraftBundle = z.object({
  mine: DraftPayload.nullable(),
  others: z.array(DraftPayload.extend({ author_id: z.uuid(), author_name: z.string() })),
});
export type DraftBundle = z.infer<typeof DraftBundle>;

/** GET /api/v1/me/drafts — รายการร่างค้างของฉันทุกการ์ด เรียง updated_at ล่าสุดก่อน */
export const MyDraftItem = z.object({
  ticket_id: z.string(), // public id
  kind: DraftKind,
  shared: z.boolean(),
  updated_at: z.iso.datetime(),
  /** ข้อมูลการ์ดพอให้แสดงแถว */
  ticket: z.object({
    subject: z.string(),
    status_code: z.string(),
    holder_team_code: z.string(),
  }),
});
export type MyDraftItem = z.infer<typeof MyDraftItem>;

export const MyDrafts = z.object({ items: z.array(MyDraftItem) });
export type MyDrafts = z.infer<typeof MyDrafts>;

/**
 * [T-062 R5] ฟิลด์ร่างบนแถวรายการ — staff เท่านั้น (customer view ไม่ได้ฟิลด์นี้เลย)
 * - has_draft = ผู้เรียกมีร่างค้างของการ์ดนี้ (kind ใดก็ได้)
 * - shared_draft_by = ชื่อคนอื่นที่กำลังร่างแบบแชร์ (ล่าสุด) ให้ทีมเห็นว่ามีคนทำอยู่
 */
export const TicketDraftMeta = z.object({
  has_draft: z.boolean(),
  shared_draft_by: z.string().nullable().optional(),
});
export type TicketDraftMeta = z.infer<typeof TicketDraftMeta>;

/** TicketSummary บนรายการของ staff เมื่อแนบข้อมูลร่างแล้ว (attachDraftMeta ของ worker) */
export type TicketSummaryWithDrafts = TicketSummary & TicketDraftMeta;
